/* W10 — Governance Intake : Document → Extraction IA → Proposition (provenance
 * verbatim) → Validation humaine → Registre. Rien ne se matérialise sans humain. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { FileSearch, Loader2, Check, X, Pencil, ShieldAlert, ListChecks } from "lucide-react";

const KIND_META = {
  risk: { label: "Risque", icon: ShieldAlert, cls: "border-[#E24B4A]/40 text-[#E24B4A]" },
  control: { label: "Contrôle", icon: ListChecks, cls: "border-[#1D9E75]/40 text-[#1D9E75]" },
};
const STATUS_LABEL = { proposed: "À valider", accepted: "Accepté", edited_accepted: "Accepté (édité)", rejected: "Rejeté" };

function ProposalCard({ p, onDone }) {
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState({});
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const meta = KIND_META[p.kind] || KIND_META.risk;
  const val = (k) => edits[k] ?? p.payload[k] ?? "";
  const act = async (fn, okMsg, materialized = false) => {
    setBusy(true);
    try { await fn(); notify.success(okMsg); onDone(materialized); } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };
  const accept = () => act(() => api.post(`/workspace/gov-intake/proposals/${p.id}/accept`,
    { edits: Object.keys(edits).length ? edits : null }),
    "Validé — matérialisé au registre avec provenance.", true);
  const reject = () => reason.trim()
    ? act(() => api.post(`/workspace/gov-intake/proposals/${p.id}/reject`, { reason: reason.trim() }), "Proposition rejetée (conservée pour audit).")
    : notify.error("Motif de rejet requis (auditabilité).");
  const done = p.status !== "proposed";
  return (
    <div className={`noam-card p-3 space-y-2 ${done ? "opacity-70" : ""}`} data-testid={`gov-proposal-${p.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full border flex items-center gap-1 ${meta.cls}`}>
          <meta.icon className="w-2.5 h-2.5" /> {meta.label}
        </span>
        {editing ? (
          <input value={val("title")} onChange={(e) => setEdits({ ...edits, title: e.target.value })} className="noam-input text-xs flex-1 min-w-[200px]" data-testid={`gov-proposal-title-edit-${p.id}`} />
        ) : (
          <span className="text-sm text-zinc-100 flex-1">{p.final_payload?.title || p.payload.title}</span>
        )}
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${p.status === "proposed" ? "border-[#EF9F27]/40 text-[#EF9F27]" : p.status === "rejected" ? "border-[#E24B4A]/40 text-[#E24B4A]" : "border-[#1D9E75]/40 text-[#1D9E75]"}`}>
          {STATUS_LABEL[p.status]}
        </span>
      </div>
      {(p.payload.description || editing) && (
        editing
          ? <textarea value={val("description")} onChange={(e) => setEdits({ ...edits, description: e.target.value })} className="noam-input text-xs w-full" rows={2} />
          : <div className="text-xs text-zinc-400">{p.payload.description}</div>
      )}
      <div className="flex gap-3 text-[10px] text-zinc-500 flex-wrap">
        {p.kind === "risk" && (editing ? (
          <>
            <label className="flex items-center gap-1">Catégorie <input value={val("categorie")} onChange={(e) => setEdits({ ...edits, categorie: e.target.value })} className="noam-input text-[10px] w-28" /></label>
            <label className="flex items-center gap-1">Impact <input type="number" min="1" max="5" value={val("impact_brut")} onChange={(e) => setEdits({ ...edits, impact_brut: Number(e.target.value) })} className="noam-input text-[10px] w-12" /></label>
            <label className="flex items-center gap-1">Proba <input type="number" min="1" max="5" value={val("probabilite_brute")} onChange={(e) => setEdits({ ...edits, probabilite_brute: Number(e.target.value) })} className="noam-input text-[10px] w-12" /></label>
          </>
        ) : (
          <><span>{p.payload.categorie}</span><span>Impact {p.payload.impact_brut}/5</span><span>Proba {p.payload.probabilite_brute}/5</span></>
        ))}
        {p.kind === "control" && <><span>{p.payload.type}</span><span>{p.payload.frequence}</span></>}
      </div>
      <div className="p-2 rounded bg-white/[0.03] border-l-2 border-[#7F77DD]/50 text-[11px]" data-testid={`gov-proposal-provenance-${p.id}`}>
        <div className="text-zinc-200 italic">« {p.provenance.excerpt} »</div>
        <div className="text-zinc-400 mt-1">
          {p.provenance.document_name || p.provenance.source_label || "Texte fourni"} · {p.provenance.justification}
        </div>
      </div>
      {p.status === "rejected" && p.reject_reason && <div className="text-[10px] text-[#E24B4A]">Rejeté : {p.reject_reason} — par {p.decided_by_email}</div>}
      {p.materialized && <div className="text-[10px] text-[#1D9E75]">Matérialisé : {p.materialized.code || p.materialized.collection} — par {p.decided_by_email}</div>}
      {!done && (
        <div className="flex gap-1.5 flex-wrap items-center">
          <button onClick={accept} disabled={busy} className="btn-primary text-[10px]" data-testid={`gov-proposal-accept-${p.id}`}>
            <Check className="w-3 h-3" /> {Object.keys(edits).length ? "Valider (édité)" : "Valider au registre"}
          </button>
          <button onClick={() => setEditing(!editing)} className="btn-ghost text-[10px] border border-white/10" data-testid={`gov-proposal-edit-${p.id}`}>
            <Pencil className="w-3 h-3" /> {editing ? "Fin d'édition" : "Éditer"}
          </button>
          {rejecting ? (
            <>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif de rejet *" className="noam-input text-[10px] flex-1 min-w-[160px]" data-testid={`gov-proposal-reject-reason-${p.id}`} />
              <button onClick={reject} disabled={busy} className="btn-ghost text-[10px] text-[#E24B4A] border border-[#E24B4A]/30" data-testid={`gov-proposal-reject-confirm-${p.id}`}><X className="w-3 h-3" /> Confirmer le rejet</button>
            </>
          ) : (
            <button onClick={() => setRejecting(true)} className="btn-ghost text-[10px] text-[#E24B4A]" data-testid={`gov-proposal-reject-${p.id}`}><X className="w-3 h-3" /> Rejeter</button>
          )}
        </div>
      )}
    </div>
  );
}

export function GovIntakeTab({ onMaterialized }) {
  const [docs, setDocs] = useState([]);
  const [docId, setDocId] = useState("");
  const [text, setText] = useState("");
  const [label, setLabel] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [proposals, setProposals] = useState(null);
  const [filter, setFilter] = useState("proposed");
  const [batchBusy, setBatchBusy] = useState(false);

  const load = () => {
    const p = filter === "accepted" ? "?status_in=accepted,edited_accepted" : (filter ? `?status=${filter}` : "");
    return api.get(`/workspace/gov-intake/proposals${p}`)
      .then((r) => setProposals(r.data.items)).catch((e) => notify.error(fmtError(e)));
  };
  const onDone = (materialized) => { load(); if (materialized && onMaterialized) onMaterialized(); };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line
  useEffect(() => { api.get("/workspace/documents").then((r) => setDocs(r.data.items || r.data || [])).catch(() => {}); }, []);

  const analyze = async () => {
    if (!docId && text.trim().length < 80) return notify.error("Choisissez un document du Drive ou collez au moins 80 caractères.");
    setAnalyzing(true);
    try {
      // W17 pilote — extraction asynchrone : start + polling (jamais de timeout HTTP)
      const start = await api.post("/workspace/gov-intake/analyze/start",
        docId ? { document_id: docId } : { text: text.trim(), source_label: label.trim() || null });
      const taskId = start.data.task_id;
      for (let i = 0; i < 120; i++) {
        await new Promise((res) => setTimeout(res, 2500));
        const st = await api.get(`/workspace/gov-intake/analyze/status/${taskId}`);
        if (st.data.status === "done") {
          notify.success(`${st.data.count ?? 0} proposition(s) extraite(s) — à valider ci-dessous.`);
          setFilter("proposed");
          load();
          break;
        }
        if (st.data.status === "failed") {
          notify.error(st.data.error || "Extraction impossible.");
          break;
        }
      }
    } catch (e) { notify.error(fmtError(e)); }
    setAnalyzing(false);
  };

  return (
    <div className="space-y-4" data-testid="gov-intake-tab">
      <div className="text-[10px] px-2.5 py-1.5 rounded border border-white/10 text-zinc-400 bg-white/[0.02]">
        NOAM construit votre modèle de gouvernance À PARTIR de ce que votre organisation sait déjà :
        chaque proposition cite sa source verbatim, et RIEN n'entre au registre sans votre validation.
      </div>
      <div className="noam-card p-3 space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
          <FileSearch className="w-3.5 h-3.5 text-[#7F77DD]" /> Analyser une source
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <select value={docId} onChange={(e) => setDocId(e.target.value)} className="noam-input text-[10px] w-72" data-testid="gov-intake-doc-select">
            <option value="">Document du Drive… (ou collez un texte)</option>
            {docs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          {!docId && <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Nom de la source (ex. Politique sécurité 2026)" className="noam-input text-[10px] w-64" data-testid="gov-intake-label" />}
          <button onClick={analyze} disabled={analyzing} className="btn-primary text-xs" data-testid="gov-intake-analyze">
            {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSearch className="w-3.5 h-3.5" />} Extraire les propositions
          </button>
          {analyzing && <span className="text-[10px] text-zinc-500" data-testid="gov-intake-progress">Analyse en cours (NOAM lit le document) — jusqu'à une minute selon la taille…</span>}
        </div>
        {!docId && (
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5}
            placeholder="Collez ici une politique, un rapport d'audit, une procédure, un règlement…"
            className="noam-input text-xs w-full" data-testid="gov-intake-text" />
        )}
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {[["proposed", "À valider"], ["accepted", "Acceptées"], ["rejected", "Rejetées"], ["", "Toutes"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${filter === v ? "border-[#7F77DD]/60 text-[#7F77DD] bg-[#7F77DD]/10" : "border-white/10 text-zinc-500"}`}
            data-testid={`gov-intake-filter-${v || "all"}`}>{l}</button>
        ))}
      </div>
      {!proposals && <div className="flex justify-center py-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      {proposals && proposals.length === 0 && (
        <div className="p-8 text-center text-xs text-zinc-600 border border-dashed border-white/10 rounded-lg" data-testid="gov-intake-empty">
          Aucune proposition {STATUS_LABEL[filter]?.toLowerCase() || ""} — analysez un document pour commencer à construire votre registre.
        </div>
      )}
      {proposals && proposals.length > 0 && filter === "proposed" && (() => {
        const score = (p) => p.kind === "risk" ? (p.payload.impact_brut || 3) * (p.payload.probabilite_brute || 3) : 0;
        const batchable = proposals.filter((p) => !(p.kind === "risk" && score(p) >= 16));
        const extreme = proposals.length - batchable.length;
        const acceptBatch = async () => {
          setBatchBusy(true);
          try {
            const r = await api.post("/workspace/gov-intake/proposals/accept-batch", { ids: batchable.map((p) => p.id) });
            notify.success(`${r.data.accepted} proposition(s) validée(s) au registre.` +
              (r.data.requires_individual_review ? ` ${r.data.requires_individual_review} cotation(s) extrême(s) à confirmer individuellement.` : ""));
            onDone(true);
          } catch (e) { notify.error(fmtError(e)); }
          setBatchBusy(false);
        };
        return (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] text-zinc-400" data-testid="gov-intake-counter">
              {proposals.length} proposition(s) à valider — triées par score décroissant.
            </div>
            {batchable.length > 1 && (
              <button onClick={acceptBatch} disabled={batchBusy} className="btn-ghost text-[10px] border border-[#1D9E75]/40 text-[#1D9E75]" data-testid="gov-intake-batch-accept">
                <Check className="w-3 h-3" /> Valider les {batchable.length} non éditées
              </button>
            )}
            {extreme > 0 && (
              <span className="text-[10px] text-[#EF9F27]" data-testid="gov-intake-extreme-guard">
                Garde-fou : {extreme} cotation(s) extrême(s) (≥ 4×4) exigent une confirmation individuelle.
              </span>
            )}
          </div>
        );
      })()}
      <div className="space-y-2">
        {(proposals || [])
          .slice()
          .sort((a, b) => ((b.payload.impact_brut || 0) * (b.payload.probabilite_brute || 0)) - ((a.payload.impact_brut || 0) * (a.payload.probabilite_brute || 0)))
          .map((p) => <ProposalCard key={p.id} p={p} onDone={onDone} />)}
      </div>
    </div>
  );
}
